import React, { useState } from "react";
import {
  Card,
  Progress,
  Typography,
  Button,
  Space,
  Tooltip,
} from "antd";
import {
  CloseOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  UploadOutlined,
  UpOutlined,
  DownOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useUploadProgress } from "../../../../core/components/firstPartyData/providers/UploadProgressProvider";

const { Text } = Typography;

const MinimizedUploadCard = () => {
  const {
    uploadState,
    clearUpload,
  } = useUploadProgress();

  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleViewDetails = () => {
    if (uploadState && uploadState.emailListId) {
      clearUpload();
      // Force a full navigation by setting window.location
      window.location.href = `/audience/details/${uploadState.emailListId}`;
    }
  };

  if (!uploadState) {
    return null;
  }

  const getStatusIcon = () => {
    switch (uploadState.status) {
      case "uploading":
        return <LoadingOutlined style={{ color: "#1890ff" }} />;
      case "completed":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "failed":
        return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
      default:
        return <UploadOutlined />;
    }
  };

  const getStatusColor = () => {
    switch (uploadState.status) {
      case "uploading":
        return "#1890ff";
      case "completed":
        return "#52c41a";
      case "failed":
        return "#ff4d4f";
      default:
        return "#1890ff";
    }
  };

  const getStatusText = () => {
    switch (uploadState.status) {
      case "uploading":
        return "Uploading...";
      case "completed":
        return "Upload Complete";
      case "failed":
        return "Upload Failed";
      default:
        return "Processing";
    }
  };

  const cardStyles = {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    width: isCollapsed ? "auto" : "320px",
    zIndex: 999,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    borderRadius: "8px",
    transition: "all 0.3s ease",
  };

  const headerStyles = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: isCollapsed ? "0" : "12px",
    paddingBottom: isCollapsed ? "0" : "8px",
    borderBottom: isCollapsed ? "none" : "1px solid #f0f0f0",
  };

  return (
    <Card
      style={cardStyles}
      bodyStyle={{ padding: isCollapsed ? "12px" : "16px" }}
      bordered={false}
    >
      <div style={headerStyles}>
        <Space size="small">
          {getStatusIcon()}
          <Text strong style={{ color: getStatusColor(), fontSize: isCollapsed ? "12px" : "14px" }}>
            {isCollapsed ? `${uploadState.progress || 0}%` : getStatusText()}
          </Text>
        </Space>
        <Space size="small">
          {uploadState.status === "uploading" && (
            <Tooltip
              title={isCollapsed ? "Expand" : "Collapse"}
              open={undefined}
              key={isCollapsed ? "expand" : "collapse"}
            >
              <Button
                type="text"
                size="small"
                icon={isCollapsed ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setIsCollapsed(!isCollapsed)}
              />
            </Tooltip>
          )}
          {uploadState.status !== "uploading" && (
            <Tooltip title="View audience details">
              <Button
                type="text"
                size="small"
                icon={<ArrowRightOutlined />}
                onClick={handleViewDetails}
              />
            </Tooltip>
          )}
          {uploadState.status !== "uploading" && (
            <Tooltip title="Close">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={clearUpload}
              />
            </Tooltip>
          )}
        </Space>
      </div>

      {!isCollapsed && (
        <>
          <div style={{ marginBottom: "12px" }}>
            <Text strong style={{ fontSize: "13px", display: "block", marginBottom: "4px" }}>
              {uploadState.audienceName || "Unnamed Audience"}
            </Text>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {uploadState.dataType === "emails"
                ? "Email Addresses"
                : uploadState.dataType === "phones"
                ? "Phone Numbers"
                : "Lookalike Audience"}
            </Text>
          </div>

          {uploadState.status === "uploading" && (
            <div>
              <Progress
                percent={uploadState.progress || 0}
                status="active"
                strokeColor={getStatusColor()}
                size="small"
              />
              {uploadState.totalRecords && (
                <Text type="secondary" style={{ fontSize: "11px", display: "block", marginTop: "4px" }}>
                  {uploadState.savedRecords || 0} / {uploadState.totalRecords} records uploaded
                </Text>
              )}
            </div>
          )}

          {uploadState.status === "completed" && (
            <div
              style={{
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: "4px",
                padding: "8px",
              }}
            >
              <Text style={{ fontSize: "12px", color: "#52c41a", display: "block", marginBottom: "4px" }}>
                {uploadState.savedRecords || uploadState.totalRecords} records successfully uploaded!
              </Text>
              <Text style={{ fontSize: "11px", color: "#389e0d" }}>
                To activate this audience, go to the audience details and click "Map to Device IDs".
              </Text>
            </div>
          )}

          {uploadState.status === "failed" && (
            <div
              style={{
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: "4px",
                padding: "8px",
              }}
            >
              <Text style={{ fontSize: "12px", color: "#ff4d4f" }}>
                {uploadState.errorMessage || "Upload failed. Please try again."}
              </Text>
            </div>
          )}

          {uploadState.status === "uploading" && (
            <div style={{ marginTop: "12px" }}>
              <Text type="secondary" style={{ fontSize: "11px" }}>
                You can navigate to other pages while the upload continues in the background.
              </Text>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default MinimizedUploadCard;
